import { defaultGraphQLClient } from '@/composables/default-urql-client'
import {
    QueryKeys,
    QueryParams,
    useListQueryParams,
    usePaginatedTanstackQueryParams,
} from '@/composables/helpers'
import httpClient, { httpClientWithoutInterceptors } from '@/utils/httpClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ref, toValue, watchEffect } from 'vue'
import gql from 'graphql-tag'
import { toast } from 'vue-sonner'
import { paginatedCloudflareServersResponseSchema } from '@/lib/schema/v2/cloudflare-server'
import { PaginatedTanstackQueryParams } from '@/lib/types/v1'
import { useRoute } from 'vue-router'
import { clean_query_keys } from '@/lib/utils'
import { cloudflareDnsRecordParamKey, cloudflareServerParamKey } from '@/composables/route'

export const usePaginatedServersQuery = (
    params?: PaginatedTanstackQueryParams | (() => PaginatedTanstackQueryParams)
) => {
    const enabled = ref(true)
    const staleTime = ref(0)
    const { queryKey, key, page, limit, sort, filter, q, view, groupBy } =
        usePaginatedTanstackQueryParams(params)

    watchEffect(() => {
        const value = toValue(params)

        if (typeof value?.enabled === 'boolean') {
            enabled.value = value.enabled
        }

        if (typeof value?.staleTime === 'number') {
            staleTime.value = value.staleTime
        }
    })

    return useQuery({
        queryKey: clean_query_keys([QueryKeys.Cloudflares, QueryKeys.Paginated, queryKey, key]),
        queryFn: async () => {
            const { data } = await httpClientWithoutInterceptors.get('v1/cloudflare/servers', {
                params: {
                    page: page.value,
                    limit: limit.value,
                    sort: sort.value,
                    filter: filter.value,
                    q: q.value,
                    view: view.value,
                    group_by: groupBy.value,
                },
            })

            return paginatedCloudflareServersResponseSchema.parse(data)
        },
        select(data) {
            return paginatedCloudflareServersResponseSchema.parse(data)
        },
        enabled: enabled,
        staleTime: staleTime,
    })
}

export const useGetCloudflareServersQuery = () => {
    const staleTime = ref(1000 * 60 * 5)

    return useQuery({
        queryKey: [QueryKeys.Cloudflares],
        queryFn: async () => {
            const { data } = await httpClient.get('v1/cloudflare/servers')
            return data
        },
        staleTime: staleTime.value,
    })
}

export const useAddCloudflareServerMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (form: any) => {
            const response = await httpClient.post(`v1/cloudflare/servers`, {
                ...form,
            })

            if (response.error) {
                throw new Error(response.error)
            }

            return response
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.Cloudflares],
                })
                .then(() => {})

            toast.success('Cloudflare Server created successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
        onError: (error) => {
            toast.error('Failed to create Cloudflare Server.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useUpdateCloudflareServerMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (form: any) => {
            return await defaultGraphQLClient.mutation(
                gql`
                    mutation updateCloudflareServer(
                        $id: String!
                        $title: String!
                        $apiUrl: String!
                        $apiToken: String!
                    ) {
                        updateServer(id: $id, title: $title, api_url: $apiUrl, api_token: $apiToken)
                    }
                `,
                {
                    ...form,
                    id: form.id,
                }
            )
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.Cloudflares],
                })
                .then(() => {})

            toast.success('Cloudflare Server updated successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useAccountListQuery = (params?: QueryParams) => {
    const route = useRoute()
    const enabled = ref(true)
    const staleTime = ref(1000 * 60 * 5)
    const { key, page, limit, sort, filter, q } = useListQueryParams(params)

    watchEffect(() => {
        if (params?.staleTime?.value) {
            staleTime.value = params.staleTime.value
        }

        if (params?.enabled?.value) {
            enabled.value = params.enabled.value
        }
    })

    return useQuery({
        queryKey: [QueryKeys.CloudflareAccounts, QueryKeys.Paginated, key],
        queryFn: async () => {
            const response = await httpClient.get(
                `v1/cloudflare/${route.params.serverId}/accounts`,
                {
                    params: {
                        page: page.value,
                        limit: limit.value,
                        sort: sort.value,
                        filter: filter.value,
                        q: q.value,
                    },
                }
            )

            return response.data
        },
        enabled: enabled.value,
        staleTime: staleTime.value,
    })
}

export const useAddCloudflareAccountMutation = () => {
    const queryClient = useQueryClient()
    const route = useRoute()

    return useMutation({
        mutationFn: async (form: any) => {
            const response = await httpClient.post(
                `cloudflare/servers/${route.params.serverId}/addresses`,
                {
                    ...form,
                }
            )

            if (response.error) {
                throw new Error(response.error)
            }
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.CloudflareAccounts],
                })
                .then(() => {})

            toast.success('Cloudflare Server Account created successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
        onError: (error) => {
            toast.error('Failed to create Cloudflare Server Account.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useZoneListQuery = (params?: QueryParams) => {
    const route = useRoute()
    const enabled = ref(true)
    const staleTime = ref(1000 * 60 * 5)
    const { key, page, limit, sort, filter, q } = useListQueryParams(params)

    watchEffect(() => {
        if (params?.staleTime?.value) {
            staleTime.value = params.staleTime.value
        }

        if (params?.enabled?.value) {
            enabled.value = params.enabled.value
        }
    })

    return useQuery({
        queryKey: [QueryKeys.CloudflareZones, QueryKeys.Paginated, key],
        queryFn: async () => {
            const { data } = await httpClient.get(`v1/cloudflare/${route.params.serverId}/zones`, {
                params: {
                    page: page.value,
                    limit: limit.value,
                    sort: sort.value,
                    filter: filter.value,
                    q: q.value,
                },
            })

            return data
        },
        enabled: enabled.value,
        staleTime: staleTime.value,
    })
}

export const useAddCloudflareZoneMutation = () => {
    const queryClient = useQueryClient()
    const route = useRoute()

    return useMutation({
        mutationFn: async ({ data }: any) => {
            const response = await httpClient.post(`v1/cloudflare/${route.params.serverId}/zones`, {
                ...data,
                account_id: route.params.accountId,
            })

            if (response.error) {
                throw new Error(response.error)
            }
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.CloudflareZones],
                })
                .then(() => {})

            toast.success('Cloudflare Server Zone created successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
        onError: (error) => {
            toast.error('Failed to create Cloudflare Server Zone.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useRecordListQuery = (params?: QueryParams) => {
    const route = useRoute()
    const enabled = ref(true)
    const staleTime = ref(1000 * 60 * 5)
    const { key, page, limit, sort, filter, q } = useListQueryParams(params)

    watchEffect(() => {
        if (params?.staleTime?.value) {
            staleTime.value = params.staleTime.value
        }

        if (params?.enabled?.value) {
            enabled.value = params.enabled.value
        }
    })

    return useQuery({
        queryKey: [QueryKeys.CloudflareZoneRecords, QueryKeys.Paginated, key],
        queryFn: async () => {
            const { data } = await httpClient.get(
                `v1/cloudflare/${route.params.serverId}/dns/records`,
                {
                    params: {
                        page: page.value,
                        limit: limit.value,
                        sort: sort.value,
                        filter: filter.value,
                        q: q.value,
                    },
                }
            )

            return data
        },
        enabled: enabled.value,
        staleTime: staleTime.value,
    })
}

export const useAddCloudflareZoneRecordMutation = () => {
    const queryClient = useQueryClient()
    const route = useRoute()

    return useMutation({
        mutationFn: async ({ data }: any) => {
            return await httpClient.post(`v1/cloudflare/${route.params.serverId}/dns/records`, {
                ...data,
                zone_uuid: route.params.zoneId,
            })
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.CloudflareZoneRecords],
                })
                .then(() => {})

            toast.success('DNS Record created successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useUpdateCloudflareZoneRecordMutation = () => {
    const queryClient = useQueryClient()
    const route = useRoute()

    return useMutation({
        mutationFn: async ({ data }: any) => {
            return await httpClient.patch(
                `v1/cloudflare/${route.params[cloudflareServerParamKey]}/dns/records/${route.params[cloudflareDnsRecordParamKey]}`,
                {
                    ...data,
                }
            )
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.CloudflareZoneRecords],
                })
                .then(() => {})

            toast.success('DNS Record updated successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}
